import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

export interface FefoOverrideDialogData {
  skuCode: string;
  chosenLot?: string;
  suggestedLot?: string;
}

export interface FefoOverrideDialogResult {
  confirmed: boolean;
  reason?: string;
}

@Component({
  selector: 'app-fefo-override-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    TextareaModule,
    AppButtonComponent,
  ],
  templateUrl: './fefo-override-dialog.component.html',
})
export class FefoOverrideDialogComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);

  @Input() visible = false;
  @Input() data: FefoOverrideDialogData | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() result = new EventEmitter<FefoOverrideDialogResult>();

  reasonForm = this.fb.group({
    reason: ['', [Validators.required, Validators.maxLength(500)]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reasonForm.reset({ reason: '' });
    }
  }

  onConfirm(): void {
    if (this.reasonForm.invalid) {
      this.reasonForm.markAllAsTouched();
      return;
    }
    const reason = this.reasonForm.get('reason')?.value?.trim() || '';
    this.result.emit({ confirmed: true, reason });
    this.close();
  }

  onCancel(): void {
    this.result.emit({ confirmed: false });
    this.close();
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  get title(): string {
    return this.translate.instant('stock.fefo.override_title');
  }

  get message(): string {
    return this.translate.instant('stock.fefo.override_message', {
      sku: this.data?.skuCode || '',
      chosen: this.data?.chosenLot || '',
      suggested: this.data?.suggestedLot || '',
    });
  }
}
